import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBillingConfig, updateBillingConfig, previewPendingCharges, runInvoicing } from '../../api/invoicing';
import { updateBranding } from '../../api/customers';
import { useAuth } from '../../auth/useAuth';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { IssueCreditNoteModal } from '../invoicing/IssueCreditNoteModal';
import type { BillingCadence, ChargeType, InvoiceFrequency } from '../../types/api';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function money(value: number) {
  return `£${value.toFixed(2)}`;
}

export function BillingSection({ orgId, invoiceEmail }: { orgId: string; invoiceEmail: string | null }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [invoiceEmailInput, setInvoiceEmailInput] = useState(invoiceEmail ?? '');
  const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false);

  useEffect(() => {
    setInvoiceEmailInput(invoiceEmail ?? '');
  }, [invoiceEmail]);

  const invoiceEmailMutation = useMutation({
    mutationFn: () => updateBranding(orgId, { invoiceEmail: invoiceEmailInput.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', orgId] });
      showToast('Invoice email updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update the invoice email.')),
  });

  const configQuery = useQuery({ queryKey: ['billing-config', orgId], queryFn: () => getBillingConfig(orgId) });

  const [defaultChargeType, setDefaultChargeType] = useState<ChargeType>('ONE_OFF');
  const [vatRate, setVatRate] = useState('20');
  const [billingCadence, setBillingCadence] = useState<BillingCadence>('MONTHLY');
  const [billingDayOfMonth, setBillingDayOfMonth] = useState('1');
  const [billingDayOfWeek, setBillingDayOfWeek] = useState('1');
  const [defaultManagedTermMonths, setDefaultManagedTermMonths] = useState('36');
  const [defaultManagedFrequency, setDefaultManagedFrequency] = useState<InvoiceFrequency>('MONTHLY');

  useEffect(() => {
    const c = configQuery.data;
    if (!c) return;
    setDefaultChargeType(c.defaultChargeType);
    setVatRate(c.vatRate);
    setBillingCadence(c.billingCadence);
    setBillingDayOfMonth(String(c.billingDayOfMonth));
    setBillingDayOfWeek(String(c.billingDayOfWeek ?? 1));
    setDefaultManagedTermMonths(String(c.defaultManagedTermMonths));
    setDefaultManagedFrequency(c.defaultManagedFrequency);
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateBillingConfig(orgId, {
        defaultChargeType,
        vatRate: Number(vatRate),
        billingCadence,
        billingDayOfMonth: billingCadence !== 'WEEKLY' ? Number(billingDayOfMonth) : undefined,
        billingDayOfWeek: billingCadence === 'WEEKLY' ? Number(billingDayOfWeek) : undefined,
        defaultManagedTermMonths: Number(defaultManagedTermMonths),
        defaultManagedFrequency,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-config', orgId] });
      showToast('Billing settings updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update billing settings.')),
  });

  const previewQuery = useQuery({
    queryKey: ['pending-charges', orgId],
    queryFn: () => previewPendingCharges(orgId),
    enabled: false,
  });

  const runMutation = useMutation({
    mutationFn: () => runInvoicing(orgId),
    onSuccess: (result) => {
      const r = Array.isArray(result) ? result[0] : result;
      setRunResult(r?.invoice ? `Invoice generated: ${r.invoice.id}` : `Nothing billed - ${r?.skippedReason ?? 'no charges due'}`);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['billing-config', orgId] });
      showToast('Invoicing run complete.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not run invoicing.')),
  });

  if (configQuery.isLoading) {
    return (
      <Card>
        <div className="py-14 text-center">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (!configQuery.data) return null;
  const config = configQuery.data;

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium text-slate-600">How and when this customer is billed.</span>
      </CardHeader>
      <CardBody className="flex flex-col gap-6">
        {error && <Alert tone="error">{error}</Alert>}

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Select
            label="Default charge type"
            value={defaultChargeType}
            onChange={(e) => setDefaultChargeType(e.target.value as ChargeType)}
          >
            <option value="ONE_OFF">One-off</option>
            <option value="MANAGED">Managed</option>
          </Select>
          <Input label="VAT rate (%)" type="number" min="0" max="100" step="0.1" value={vatRate} onChange={(e) => setVatRate(e.target.value)} />
          <Select
            label="Billing cadence"
            value={billingCadence}
            onChange={(e) => setBillingCadence(e.target.value as BillingCadence)}
          >
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="QUARTERLY">Quarterly</option>
          </Select>

          {billingCadence === 'WEEKLY' ? (
            <Select label="Day of week" value={billingDayOfWeek} onChange={(e) => setBillingDayOfWeek(e.target.value)}>
              {DAYS_OF_WEEK.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              label="Day of month"
              type="number"
              min="1"
              max="28"
              value={billingDayOfMonth}
              onChange={(e) => setBillingDayOfMonth(e.target.value)}
            />
          )}

          <Input
            label="Default managed term (months)"
            type="number"
            min="1"
            value={defaultManagedTermMonths}
            onChange={(e) => setDefaultManagedTermMonths(e.target.value)}
          />
          <Select
            label="Default managed frequency"
            value={defaultManagedFrequency}
            onChange={(e) => setDefaultManagedFrequency(e.target.value as InvoiceFrequency)}
          >
            <option value="MONTHLY">Monthly</option>
            <option value="ANNUAL">Annual</option>
          </Select>
        </div>

        <div className="flex items-end gap-3 border-t border-slate-100 pt-4">
          <div className="flex-1">
            <Input
              label="Invoice email address"
              type="email"
              placeholder="accounts@customer.com"
              hint="Where invoice emails are sent. Leave blank to send to this customer's Customer Admin(s) instead."
              value={invoiceEmailInput}
              onChange={(e) => setInvoiceEmailInput(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            isLoading={invoiceEmailMutation.isPending}
            onClick={() => invoiceEmailMutation.mutate()}
            disabled={invoiceEmailInput.trim() === (invoiceEmail ?? '')}
          >
            Save
          </Button>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4 text-sm text-slate-500">
          <span>
            Next billing date: <strong className="text-slate-700">{new Date(config.nextBillingDate).toLocaleDateString()}</strong>
            {config.lastBilledAt && (
              <>
                {' '}
                &middot; Last billed: {new Date(config.lastBilledAt).toLocaleDateString()}
              </>
            )}
          </span>
          <Button size="sm" isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            Save
          </Button>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Pending charges</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                isLoading={previewQuery.isFetching}
                onClick={() => {
                  setShowPreview(true);
                  previewQuery.refetch();
                }}
              >
                Preview
              </Button>
              {user?.isMspAdmin && (
                <Button size="sm" isLoading={runMutation.isPending} onClick={() => runMutation.mutate()}>
                  Bill now
                </Button>
              )}
              {user?.isMspAdmin && (
                <Button size="sm" variant="secondary" onClick={() => setCreditNoteModalOpen(true)}>
                  Issue credit note
                </Button>
              )}
            </div>
          </div>

          {runResult && <p className="mt-2 text-sm text-slate-500">{runResult}</p>}

          {showPreview && previewQuery.data && (
            <div className="mt-3 flex flex-col gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
              {previewQuery.data.oneOffCharges.length === 0 &&
              previewQuery.data.managedCharges.length === 0 &&
              previewQuery.data.adHocCharges.length === 0 ? (
                <p className="text-slate-400">Nothing pending right now.</p>
              ) : (
                <>
                  {previewQuery.data.oneOffCharges.map((c) => (
                    <div key={c.orderLineId} className="flex justify-between">
                      <span className="text-slate-600">{c.description}</span>
                      <span className="font-medium text-slate-800">{money(c.netAmount)}</span>
                    </div>
                  ))}
                  {previewQuery.data.managedCharges.map((c) => (
                    <div key={c.invoiceScheduleId} className="flex justify-between">
                      <span className="text-slate-600">
                        {c.description} &times; {c.occurrencesDue}
                      </span>
                      <span className="font-medium text-slate-800">{money(c.netAmount)}</span>
                    </div>
                  ))}
                  {previewQuery.data.adHocCharges.map((c) => (
                    <div key={c.adHocChargeId} className="flex justify-between">
                      <span className="text-slate-600">
                        {c.description} &times; {c.occurrencesDue}
                      </span>
                      <span className="font-medium text-slate-800">{money(c.netAmount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900">
                    <span>Total (incl. VAT)</span>
                    <span>{money(previewQuery.data.grandTotal)}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </CardBody>

      <IssueCreditNoteModal orgId={orgId} open={creditNoteModalOpen} onClose={() => setCreditNoteModalOpen(false)} />
    </Card>
  );
}
